import React, { useMemo } from 'react';
import _round from 'lodash/round';

import { Chart } from './Chart';
import { ChartOrEmptyMessage } from '../..';

const playsKey = 'plays' as keyof ProShow;
const earningsKey = 'earnings' as keyof ProShow;
const minEarningsPerShow = 0.01;
const minEarningsPercentage = 0.01;

/**
 * Show only shows with earnings of $0.01 or greater.
 * Group shows with less than 1% of total earnings into a single item called "<n> Shows < 1%".
 */
function dataAdapter(shows: ProShow[], truncateChartData: boolean): ProShow[] {
    let total_earnings = 0;
    const showsAboveMinEarnings = [] as ProShow[];
    // `dedupedShows` is an object where the key is a show_name and the value is a deduped ProShow
    // for that show_name
    const dedupedShows = shows.reduce(
        (
            acc: Record<string, ProShow>,
            show: ProShow,
        ): Record<string, ProShow> => {
            if (show.show_name) {
                let showName = show.show_name;

                if (
                    showName.includes('not_reported') ||
                    showName.includes('not reported')
                ) {
                    showName = 'Program not reported';
                }

                if (acc[showName] === undefined) {
                    acc[showName] = { ...show, show_name: showName };
                } else {
                    for (const key of [playsKey, earningsKey]) {
                        if (show[key] !== null) {
                            if (acc[showName][key] !== null) {
                                // eslint-disable-next-line
                                //@ts-ignore
                                acc[showName][key] += show[key];
                            } else {
                                // eslint-disable-next-line
                                //@ts-ignore
                                acc[showName][key] = show[key];
                            }
                        }
                    }
                }
            }

            return acc;
        },
        {} as Record<string, ProShow>,
    );

    if (!truncateChartData) {
        return Object.values(dedupedShows);
    }

    Object.values(dedupedShows).forEach((show: ProShow): void => {
        if (
            show.earnings !== null &&
            show.earnings !== undefined &&
            show.earnings >= minEarningsPerShow
        ) {
            showsAboveMinEarnings.push(show);
            total_earnings += show.earnings;
        }
    });

    const filteredShows = [] as ProShow[];
    const summaryShow = {
        earnings: 0,
        plays: 0,
        show_name: '',
    };
    let summaryShowCount = 0;

    showsAboveMinEarnings.forEach((show: ProShow): void => {
        if (
            show.earnings !== null &&
            show.earnings !== undefined &&
            show.earnings / total_earnings > minEarningsPercentage
        ) {
            filteredShows.push(show);
        } else if (show.earnings !== null && show.earnings !== undefined) {
            summaryShowCount += 1;
            summaryShow.earnings += show.earnings;

            if (show.plays !== null && show.plays !== undefined) {
                summaryShow.plays += show.plays;
            }
        }
    });

    summaryShow.earnings = _round(summaryShow.earnings, 2);

    const showsSingularOrPlural = summaryShowCount === 1 ? 'show' : 'shows';
    const percentageTextSingularOrPlural =
        summaryShowCount === 1 ? 'with < 1%' : 'with < 1% per show';

    summaryShow.show_name = `${summaryShowCount} ${showsSingularOrPlural} ${percentageTextSingularOrPlural}`;

    if (summaryShowCount > 0) {
        filteredShows.push(summaryShow);
    }

    return filteredShows;
}

/**
 * @typedef {EarningsPerShowProps}
 * @property {string} chartDivId a unique ID for this chart instance
 * @property {number} height chart height
 * @property {ProShow[]} shows array of ProShow objects
 * @property {boolean} truncateChartData whether to summarize small values into a single show
 */
interface EarningsPerShowProps {
    chartDivId: string;
    height: number;
    shows: ProShow[];
    truncateChartData: boolean;
}

export const EarningsPerShow = ({
    chartDivId,
    height,
    shows,
    truncateChartData,
}: EarningsPerShowProps): JSX.Element => {
    const filteredShows = useMemo(
        () => dataAdapter(shows, truncateChartData),
        [shows],
    );

    return (
        <ChartOrEmptyMessage data={filteredShows} height={height}>
            <Chart
                chartDivId={chartDivId}
                height={height}
                shows={filteredShows}
            />
        </ChartOrEmptyMessage>
    );
};
